/**
 * Owned suspension of the Phaser keyboard.
 *
 * Several parts of the UI need the arrow keys and WASD kept away from the
 * avatar: a focused text field (typing), an open modal, and full-screen video
 * (an arrow key would walk the avatar out of the private area and end the
 * call). Each of them used to flip `keyboard.enabled` on its own, so whoever
 * finished last won: closing the chat or a modal switched movement back on
 * underneath full-screen video.
 *
 * Now each one takes a *hold* and gives it back. The keyboard is enabled
 * exactly when nobody holds it, and every path that used to force it on
 * ({@link restorePhaserKeyboard}) asks instead.
 *
 * The holds are keyed by the game object in a `WeakMap`, so they die with the
 * game and nothing is written onto Phaser. Anything that disables the
 * keyboard *without* a hold is overruled by the next release or restore.
 */

/** The slice of a Phaser game this module touches. */
export interface KeyboardGame {
  input?: {
    keyboard?: { enabled: boolean } | null;
  } | null;
  scene?: {
    /** Active scenes; each has its own KeyboardPlugin */
    getScenes?: (isActive?: boolean) => Array<{
      input?: { keyboard?: { resetKeys?: () => void } | null } | null;
    }>;
  } | null;
}

/**
 * Why the keyboard is suspended. The known reasons are listed for readers;
 * any string works.
 */
export type KeyboardSuspendReason =
  | 'text-field'
  | 'modal'
  | 'video-fullscreen'
  | (string & {});

const holdsByGame = new WeakMap<object, Map<symbol, KeyboardSuspendReason>>();

function holdsOf(game: KeyboardGame): Map<symbol, KeyboardSuspendReason> {
  let holds = holdsByGame.get(game);
  if (!holds) {
    holds = new Map();
    holdsByGame.set(game, holds);
  }
  return holds;
}

/** Enabled exactly when nothing holds the keyboard. */
function applyHolds(game: KeyboardGame): boolean {
  const enabled = (holdsByGame.get(game)?.size ?? 0) === 0;
  const keyboard = game.input?.keyboard;
  if (keyboard) {
    keyboard.enabled = enabled;
  }
  return enabled;
}

/**
 * Clears Key.isDown on every active scene.
 *
 * The KeyboardManager drops keyup events while disabled, so a key that is held
 * when the keyboard is suspended would otherwise stay "down" for as long as it
 * is suspended (the avatar keeps walking) and after (it walks with nothing
 * pressed until that key is pressed and released again).
 */
function resetSceneKeys(game: KeyboardGame): void {
  const scenes = game.scene?.getScenes?.(true) ?? [];
  for (const scene of scenes) {
    scene?.input?.keyboard?.resetKeys?.();
  }
}

/**
 * Takes the keyboard away from the game for `reason` and returns the function
 * that gives it back. Releasing is idempotent; the keyboard comes back only
 * once every hold is released.
 *
 * A no-op before the game exists (pre-game screens), so callers need not
 * check.
 */
export function suspendPhaserKeyboard(
  game: KeyboardGame | null | undefined,
  reason: KeyboardSuspendReason
): () => void {
  if (!game) return () => {};

  const holds = holdsOf(game);
  const token = Symbol(reason);
  holds.set(token, reason);
  applyHolds(game);
  resetSceneKeys(game);

  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds.delete(token);
    applyHolds(game);
  };
}

/**
 * Turns the keyboard back on unless something still holds it. For the paths
 * that used to set `keyboard.enabled = true` as a failsafe (closing the chat,
 * clicking the canvas).
 *
 * @returns Whether the keyboard is enabled now
 */
export function restorePhaserKeyboard(game: KeyboardGame | null | undefined): boolean {
  if (!game) return true;
  return applyHolds(game);
}

/** The reasons currently holding the keyboard, oldest first. */
export function phaserKeyboardHolds(game: KeyboardGame | null | undefined): KeyboardSuspendReason[] {
  if (!game) return [];
  return [...(holdsByGame.get(game)?.values() ?? [])];
}
